<script setup lang="ts">
import type { MapProvider } from '@gpx-spa/geo-domain';
import Minus from '@primeicons/vue/minus';
import Plus from '@primeicons/vue/plus';
import Button from 'primevue/button';
import { computed, nextTick, onMounted, onUnmounted, ref, toRef } from 'vue';
import { useGpxMapProvider } from '../composables/useGpxMapProvider';

const props = defineProps<{
  mapProvider?: MapProvider;
  minZoom?: number;
  maxZoom?: number;
}>();

const map = useGpxMapProvider(toRef(props, 'mapProvider'));

/** Map zoom is not reactive; bump when the view changes so disabled state stays correct. */
const viewRevision = ref(0);

const minZ = computed(() => props.minZoom ?? 1);
const maxZ = computed(() => props.maxZoom ?? 20);

const canZoomIn = computed(() => {
  viewRevision.value;
  const z = map.value.getZoom();
  if (z === undefined) return true;
  return z < maxZ.value;
});

const canZoomOut = computed(() => {
  viewRevision.value;
  const z = map.value.getZoom();
  if (z === undefined) return true;
  return z > minZ.value;
});

function zoomIn() {
  const z = map.value.getZoom();
  if (z === undefined) return;
  if (z >= maxZ.value) return;
  map.value.setZoom(Math.min(maxZ.value, z + 1));
  viewRevision.value++;
}

function zoomOut() {
  const z = map.value.getZoom();
  if (z === undefined) return;
  if (z <= minZ.value) return;
  map.value.setZoom(Math.max(minZ.value, z - 1));
  viewRevision.value++;
}

onMounted(() => {
  const p = map.value;
  const offMoveEnd = p.onMoveEnd(() => {
    viewRevision.value++;
  });
  onUnmounted(offMoveEnd);

  queueMicrotask(() => {
    viewRevision.value++;
  });
  void nextTick().then(async () => {
    viewRevision.value++;
    await nextTick();
    viewRevision.value++;
  });
});
</script>

<template>
  <!-- Same vertical gap as MapsDemoView stack (gap-1 between zoom / fit / center). -->
  <div class="flex flex-col gap-1">
    <Button
      rounded
      severity="contrast"
      size="small"
      :disabled="!canZoomIn"
      aria-label="Zoom in"
      @click="zoomIn"
    >
      <template #icon>
        <Plus />
      </template>
    </Button>
    <Button
      rounded
      severity="contrast"
      size="small"
      :disabled="!canZoomOut"
      aria-label="Zoom out"
      @click="zoomOut"
    >
      <template #icon>
        <Minus />
      </template>
    </Button>
  </div>
</template>
